import React, {
  useCallback, useEffect, useMemo, useRef, memo, useState,
} from 'react';
import PropTypes from 'prop-types';
import cl from 'classnames';
import Swiper from 'swiper';
import 'swiper/dist/css/swiper.min.css';
import TariffTableItem from './TariffTableItem';
import {mediaSizes} from '../../configs/media';
import {USER_ROLE} from '../../constants/user';
import {CANDIDATE_SERVICE_KEYS, EMPLOYER_SERVICE_KEYS} from '../../constants/tariffs';
import {t} from '../../services/i18nHandler';

function TariffsTableViewList({
  tariffs, services, activeRole, activePeriod, detailsClick, language, serviceOptions, landing,
}) {
  const swiper = useRef();

  const swiperRef = useRef();

  const progressBarRef = useRef();

  const nextBtn = useRef();

  const prevBtn = useRef();

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const onWindowResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);

  const servicesKeyOptionsByRole = useMemo(() => {
    switch (activeRole) {
    case USER_ROLE.CANDIDATE: return serviceOptions.filter((key) => (CANDIDATE_SERVICE_KEYS.includes(key.value)));
    case USER_ROLE.EMPLOYER:
    case USER_ROLE.RECRUITER: return serviceOptions.filter((key) => (EMPLOYER_SERVICE_KEYS.includes(key.value)));
    default: return [];
    }
  }, [serviceOptions, activeRole]);

  const destroySwiper = useCallback(() => {
    if (swiper.current) {
      swiper.current.destroy(true, true);
    }
  }, []);

  const initSwiper = useCallback(() => {
    destroySwiper();
    swiper.current = new Swiper(swiperRef.current, {
      slidesPerView: 'auto',
      spaceBetween: 20,
      pagination: {
        el: progressBarRef.current,
        type: 'progressbar',
      },
      navigation: {
        nextEl: nextBtn.current,
        prevEl: prevBtn.current,
      },
    });
  }, []);

  const allServices = useMemo(() => ([...tariffs, ...services]), [tariffs, services]);

  const payInitSwiper = useMemo(() => (allServices.length > 5
    || (windowWidth < mediaSizes.xx && allServices.length > 1)), [allServices, windowWidth]);

  useEffect(() => {
    if (payInitSwiper) {
      initSwiper();
    } else {
      destroySwiper();
    }
  }, [payInitSwiper, allServices.length]);

  useEffect(() => {
    window.addEventListener('resize', onWindowResize);
    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, []);

  return (
    <div className={cl('tariffs_table-view', {
      landing,
    })}
    >
      <div className="tariffs_table-view_container">
        <div
          className={cl('tariffs_table-view_services', {
            init: payInitSwiper,
          })}
        >
          {servicesKeyOptionsByRole.map((option) => {
            const optionText = option.text || {};
            return (
              <div
                className="tariffs_table-view_services_item"
                key={option._id}
              >
                {optionText[language]}
              </div>
            );
          })}
          <div className="tariffs_table-view_services_item">
            {t('tariffs.totalHours')}
          </div>
        </div>
        <div className={cl('tariffs_table-view_list', {
          init: payInitSwiper,
        })}
        >
          {!payInitSwiper && (
            <>
              {allServices.map((s) => (
                <TariffTableItem
                  key={s._id}
                  tariff={s}
                  detailsClick={() => detailsClick(s)}
                  detailsDemoClick={() => detailsClick(s, true)}
                  activePeriod={activePeriod}
                  language={language}
                  servicesKeyOptionsByRole={servicesKeyOptionsByRole}
                />
              ))}
            </>
          )}
          {payInitSwiper && (
            <div>
              <div className="swiper-container" ref={swiperRef}>
                <div className="swiper-wrapper">
                  {allServices.map((s) => (
                    <div className="swiper-slide">
                      <TariffTableItem
                        key={s._id}
                        tariff={s}
                        detailsClick={() => detailsClick(s)}
                        detailsDemoClick={() => detailsClick(s, true)}
                        activePeriod={activePeriod}
                        language={language}
                        servicesKeyOptionsByRole={servicesKeyOptionsByRole}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {payInitSwiper && (
        <div className="tariffs_table-view_progress-container">
          <div className="tariffs_table-view_progress-bar">
            <button
              type="button"
              className="carousel-big--navigation_btn carousel-prev"
              ref={prevBtn}
            >
              <i className="fa fa-angle-left" />
            </button>
            <div className="swiper-pagination-progressbar progress-line" ref={progressBarRef} />
            <button
              type="button"
              className="carousel-big--navigation_btn carousel-next"
              ref={nextBtn}
            >
              <i className="fa fa-angle-right" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
TariffsTableViewList.defaultProps = {
  tariffs: [],
  services: [],
  detailsClick: () => {},
};

TariffsTableViewList.propTypes = {
  tariffs: PropTypes.arrayOf(PropTypes.shape({})),
  services: PropTypes.arrayOf(PropTypes.shape({})),
  detailsClick: PropTypes.func,
};

export default memo(TariffsTableViewList);
